import { useState } from 'react'

import { Tooltip } from '@mantine/core'
import { RefreshCw } from 'lucide-react'

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

const formatRelative = (date: Date): string => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export interface RefreshButtonProps {
  /** Triggered when the user clicks. May return a Promise; the spinner stays
   * active until it resolves (with a small minimum duration for visual
   * feedback even on instant refetches). */
  onRefresh: () => void | Promise<void>
  /** External loading flag — useful when the parent's data fetch is already
   * in flight. The button spins continuously while this is true. */
  loading?: boolean
  /** When provided, hovering the button shows "Updated X ago" in a tooltip. */
  lastUpdatedAt?: Date | null
  ariaLabel?: string
  className?: string
  /** Minimum spin duration on click, in ms. Keeps fast refetches from feeling
   * unresponsive ("did anything happen?"). Defaults to 500. */
  minSpinMs?: number
}

export const RefreshButton = ({
  onRefresh,
  loading = false,
  lastUpdatedAt = null,
  ariaLabel = 'Refresh data',
  className,
  minSpinMs = 500,
}: RefreshButtonProps) => {
  const [busy, setBusy] = useState(false)

  const handleClick = async () => {
    if (busy || loading) return
    setBusy(true)
    const start = Date.now()
    try {
      await onRefresh()
    } finally {
      const elapsed = Date.now() - start
      const remaining = Math.max(0, minSpinMs - elapsed)
      window.setTimeout(() => setBusy(false), remaining)
    }
  }

  const isSpinning = busy || loading

  const button = (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-busy={isSpinning}
      disabled={isSpinning}
      onClick={handleClick}
      data-testid="refresh-button"
      className={join(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-doubleNickel-gray-500 transition-colors',
        'hover:border-gray-400 hover:text-doubleNickel-gray-900',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-500 focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-70',
        className
      )}
    >
      <RefreshCw
        size={16}
        className={isSpinning ? 'animate-spin' : ''}
        data-testid="refresh-icon"
      />
    </button>
  )

  if (lastUpdatedAt) {
    return (
      <Tooltip
        label={`Updated ${formatRelative(lastUpdatedAt)}`}
        position="bottom"
        withArrow
      >
        {button}
      </Tooltip>
    )
  }

  return button
}

export default RefreshButton
